import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button, Tooltip } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import dayjs from '_helpers/datetime';

// Shared week-window for every session-based table (attendance grid, manager overview,
// learning results). Shows exactly one calendar week of sessions; prev/next jump to the
// nearest week that actually has sessions (skips empty weeks); opens on the week nearest today.
// No per-page size picker — the week is the page.

const DAY = 86400000;

// Monday 00:00 (ms) of the week containing ts. dayjs().day(): 0=Sun..6=Sat.
function weekStartMs(ts) {
    const d = dayjs(ts);
    const offset = (d.day() + 6) % 7; // 0 when Monday
    return d.subtract(offset, 'day').startOf('day').valueOf();
}

/**
 * @param sessions  array of { date, ... } (any order)
 * @param stickyKey re-anchor to the nearest-today week whenever this changes (e.g. classId)
 * @returns { weekSessions, weekStart, controls, label }
 */
export function useSessionWeek(sessions, stickyKey) {
    const [weekStart, setWeekStart] = useState(null);
    const positionedRef = useRef(null);

    // Anchor = week of the first session today-or-later; else the last past session; else this week.
    const anchorWeek = useMemo(() => {
        if (!sessions.length) return weekStartMs(dayjs().valueOf());
        const today = dayjs().startOf('day').valueOf();
        const upcoming = sessions.find(s => dayjs(s.date).valueOf() >= today);
        const ref = upcoming || sessions[sessions.length - 1];
        return weekStartMs(dayjs(ref.date).valueOf());
    }, [sessions]);

    useEffect(() => {
        if (positionedRef.current !== stickyKey) {
            setWeekStart(anchorWeek);
            positionedRef.current = stickyKey;
        }
    }, [stickyKey, anchorWeek]);

    const ws = weekStart == null ? anchorWeek : weekStart;
    const we = ws + 7 * DAY;

    const weekSessions = useMemo(
        () => sessions
            .filter(s => { const t = dayjs(s.date).valueOf(); return t >= ws && t < we; })
            .sort((a, b) => new Date(a.date) - new Date(b.date)),
        [sessions, ws, we]
    );

    const hasPast = useMemo(() => sessions.some(s => dayjs(s.date).valueOf() < ws), [sessions, ws]);
    const hasFuture = useMemo(() => sessions.some(s => dayjs(s.date).valueOf() >= we), [sessions, we]);

    const prevWeek = () => {
        const before = sessions.map(s => dayjs(s.date).valueOf()).filter(t => t < ws);
        if (before.length) setWeekStart(weekStartMs(Math.max(...before)));
    };
    const nextWeek = () => {
        const after = sessions.map(s => dayjs(s.date).valueOf()).filter(t => t >= we);
        if (after.length) setWeekStart(weekStartMs(Math.min(...after)));
    };

    const label = `Tuần ${dayjs(ws).format('DD/MM')} – ${dayjs(we - DAY).format('DD/MM/YYYY')}`;

    const controls = (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Button icon={<LeftOutlined />} disabled={!hasPast} onClick={prevWeek}>Tuần trước</Button>
            <Button disabled={!hasFuture} onClick={nextWeek}>Tuần sau <RightOutlined /></Button>
            <Tooltip title="Về tuần gần hôm nay nhất">
                <Button onClick={() => setWeekStart(anchorWeek)}>Tuần này</Button>
            </Tooltip>
            <span style={{ color: 'var(--ink-4)', fontSize: 13 }}>{label} · {weekSessions.length} buổi</span>
        </div>
    );

    const goToday = () => setWeekStart(anchorWeek);

    return { weekSessions, weekStart: ws, controls, label,
             prevWeek, nextWeek, goToday, hasPast, hasFuture, count: weekSessions.length };
}
